import { type FC, useCallback } from "react";

import { Checkbox, Text, Tooltip } from "@mantine/core";
import classNames from "clsx";
import { useIntl } from "react-intl";

import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { formatBytes } from "../utils/formatBytes";

import { RemoteAssetLensUsageExpandContent } from "./RemoteAssetLensUsageExpandContent";
import { RemoteAssetPreview } from "./RemoteAssetPreview";
import {
    card,
    cardContent,
    cardCopyingSelected,
    cardDeleteSelected,
    cardDisabled,
    cardExpandSection,
    cardLensLink,
    cardMain,
    cardMeta,
    cardMetaText,
    cardMoreLink,
    cardName,
    cardUsedByToggle,
    dateText,
    lensCountActive,
    list,
    sentinel,
    typeBadge,
} from "./RemoteAssetsCardList.module.css";
import { type RemoteAssetListItem } from "./RemoteAssetsTable.types";
import { type RemoteAssetsMode } from "./RemoteAssetsToolbar";
import {
    LensUsages,
    LensUsagesToggle,
    LensUsagesToggleOpen,
    LoadingMoreAssets,
    StatusInUse,
} from "./messages.generated";

interface RemoteAssetsCardListProps {
    assets: readonly RemoteAssetListItem[];
    mode: RemoteAssetsMode;
    checkedIds: Set<string>;
    onToggleChecked: (id: string) => void;
    onRowClick: (assetId: string) => void;
    originalForkingAllowed: Map<string, boolean>;
    orgId: string;
    expandedIds: Set<string>;
    onToggleExpand: (id: string) => void;
    hasMore: boolean;
    isFetchingMore: boolean;
    onLoadMore: () => void;
}

export const RemoteAssetsCardList: FC<RemoteAssetsCardListProps> = ({
    assets,
    mode,
    checkedIds,
    onToggleChecked,
    onRowClick,
    originalForkingAllowed,
    orgId,
    expandedIds,
    onToggleExpand,
    hasMore,
    isFetchingMore,
    onLoadMore,
}) => {
    const intl = useIntl();
    const sentinelRef = useInfiniteScroll<HTMLDivElement>({ hasMore, isFetchingMore, onLoadMore });

    const isDeleteMode = mode === "delete";
    const isEditCopyingMode = mode === "editCopying";
    const showCheckboxes = isDeleteMode || isEditCopyingMode;

    const handleCardClick = useCallback(
        (asset: RemoteAssetListItem) => {
            if (isDeleteMode) {
                if (asset.lensUsages > 0) return;
                onToggleChecked(asset.id);
            } else if (isEditCopyingMode) {
                onToggleChecked(asset.id);
            } else {
                onRowClick(asset.id);
            }
        },
        [isDeleteMode, isEditCopyingMode, onToggleChecked, onRowClick],
    );

    const formatDate = (dateStr: string): string =>
        intl.formatDate(new Date(dateStr), { month: "short", day: "numeric" });

    const formatFullDate = (dateStr: string): string =>
        intl.formatDate(new Date(dateStr), {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });

    return (
        <div className={list}>
            {assets.map((asset) => {
                const isInUse = isDeleteMode && asset.lensUsages > 0;
                const isChecked = checkedIds.has(asset.id);
                const hasChanged = isEditCopyingMode && !!originalForkingAllowed.get(asset.id) !== isChecked;

                const cardClasses = classNames(
                    card,
                    isInUse && cardDisabled,
                    isDeleteMode && isChecked && cardDeleteSelected,
                    isEditCopyingMode && hasChanged && cardCopyingSelected,
                );

                return (
                    <div
                        key={asset.id}
                        className={cardClasses}
                        role="button"
                        tabIndex={isInUse ? -1 : 0}
                        onClick={() => {
                            handleCardClick(asset);
                        }}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                handleCardClick(asset);
                            }
                        }}
                    >
                        <div className={cardMain}>
                            {showCheckboxes && (
                                <Checkbox
                                    checked={isChecked}
                                    disabled={isInUse}
                                    onChange={() => {
                                        onToggleChecked(asset.id);
                                    }}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                    }}
                                />
                            )}

                            <RemoteAssetPreview type={asset.type} previewImageUrl={asset.previewImageUrl} size={40} />

                            <div className={cardContent}>
                                <Text size="sm" className={cardName}>
                                    {asset.name}
                                </Text>
                                <div className={cardMeta}>
                                    <span className={typeBadge}>{asset.type}</span>
                                    <Text className={cardMetaText}>{formatBytes(asset.assetSize)}</Text>
                                    {asset.lensUsages > 0 && mode === "normal" ? (
                                        <button
                                            type="button"
                                            className={cardUsedByToggle}
                                            aria-expanded={expandedIds.has(asset.id)}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onToggleExpand(asset.id);
                                            }}
                                        >
                                            {expandedIds.has(asset.id) ? (
                                                <LensUsagesToggleOpen count={asset.lensUsages} />
                                            ) : (
                                                <LensUsagesToggle count={asset.lensUsages} />
                                            )}
                                        </button>
                                    ) : (
                                        <Text
                                            className={`${cardMetaText} ${asset.lensUsages > 0 ? lensCountActive : ""}`}
                                        >
                                            <LensUsages count={asset.lensUsages} />
                                        </Text>
                                    )}
                                    {isInUse && (
                                        <Text className={cardMetaText}>
                                            <StatusInUse />
                                        </Text>
                                    )}
                                </div>
                            </div>

                            {mode === "normal" && (
                                <Tooltip label={formatFullDate(asset.updatedAt)}>
                                    <Text className={dateText}>{formatDate(asset.updatedAt)}</Text>
                                </Tooltip>
                            )}
                        </div>

                        {expandedIds.has(asset.id) && mode === "normal" && (
                            <RemoteAssetLensUsageExpandContent
                                assetId={asset.id}
                                lensUsages={asset.lensUsages}
                                orgId={orgId}
                                containerClassName={cardExpandSection}
                                linkClassName={cardLensLink}
                                moreClassName={cardMoreLink}
                                textSize="xs"
                            />
                        )}
                    </div>
                );
            })}
            {hasMore && (
                <div ref={sentinelRef} className={sentinel}>
                    {isFetchingMore && (
                        <Text c="dimmed" size="sm">
                            <LoadingMoreAssets />
                        </Text>
                    )}
                </div>
            )}
        </div>
    );
};
